import type { ErrorEvent, EventHint } from '@sentry/react'

const HTTP_UNAUTHORIZED = 401

// Client-side connectivity failures (offline, VPN drop, ad blocker, DNS) aren't actionable
// app bugs. Sentry's fetch instrumentation appends "(hostname)", so match by prefix.
export const NETWORK_ERROR_PATTERNS = [
  /^Failed to fetch/, // Chrome/Edge
  /^NetworkError when attempting to fetch resource\./, // Firefox
  /^Load failed/, // Safari
]

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null

const isNetworkError = (wrappedError: unknown): boolean => {
  if (!isRecord(wrappedError) || typeof wrappedError.message !== 'string') {
    return false
  }
  const { message } = wrappedError

  return NETWORK_ERROR_PATTERNS.some(pattern => pattern.test(message))
}

// callApi already answers every 401 by clearing the session and sending the agent to the
// login page, which brings them back where they were: an expired session is not a bug.
const isSessionExpired = (error: unknown): boolean => isRecord(error) && error.status === HTTP_UNAUTHORIZED

// Most sagas wrap the real fetch failure in a descriptive `new Error(...)` and attach the
// original as `extra.error`, so `ignoreErrors` — which only sees the reported message — misses it.
export const sentryBeforeSend = (event: ErrorEvent, hint?: EventHint): ErrorEvent | null => {
  const wrappedError = event.extra?.error
  if (isNetworkError(wrappedError)) {
    return null
  }
  if (isSessionExpired(wrappedError) || isSessionExpired(hint?.originalException)) {
    return null
  }

  return event
}
